import React, { useState, useRef, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import { Send, Bot, User, Loader2 } from 'lucide-react'
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { cn } from '../lib/utils'

export interface Message {
  id: string
  type: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  metadata?: {
    agentName?: string
    stage?: string
    status?: 'thinking' | 'processing' | 'complete'
  }
}

interface ChatInterfaceProps {
  messages: Message[]
  onSendMessage: (message: string) => void
  isProcessing?: boolean
  disabled?: boolean
  suggestions?: string[]
  className?: string
}

export function ChatInterface({ 
  messages, 
  onSendMessage, 
  isProcessing = false, 
  disabled = false,
  suggestions = [],
  className 
}: ChatInterfaceProps) {
  const [inputValue, setInputValue] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const renderMessageContent = (content: string, messageType: Message['type']) => {
    if (messageType === 'user') {
      return <div className="whitespace-pre-wrap text-sm">{content}</div>
    }

    return (
      <div className="text-sm leading-relaxed">
        <ReactMarkdown
          components={{
            h1: ({ children }) => (
              <h1 className="text-lg font-semibold mb-2 text-foreground">{children}</h1>
            ),
            h2: ({ children }) => (
              <h2 className="text-base font-semibold mb-2 mt-3 text-foreground">{children}</h2>
            ),
            h3: ({ children }) => (
              <h3 className="text-sm font-semibold mb-1 mt-3 text-foreground">{children}</h3>
            ),
            p: ({ children }) => (
              <p className="mb-2 last:mb-0 text-foreground whitespace-pre-wrap">{children}</p>
            ),
            ul: ({ children }) => (
              <ul className="mb-2 ml-5 list-disc space-y-1 text-foreground">{children}</ul>
            ),
            ol: ({ children }) => (
              <ol className="mb-2 ml-5 list-decimal space-y-1 text-foreground">{children}</ol>
            ),
            li: ({ children }) => <li className="text-foreground">{children}</li>,
            strong: ({ children }) => (
              <strong className="font-semibold text-foreground">{children}</strong>
            ),
            em: ({ children }) => <em className="italic text-foreground">{children}</em>,
            a: ({ children, href }) => (
              <a
                className="underline underline-offset-2 text-foreground hover:opacity-80"
                href={href}
                target="_blank"
                rel="noreferrer"
              >
                {children}
              </a>
            ),
            code: ({ children }) => (
              <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">{children}</code>
            ),
            pre: ({ children }) => (
              <pre className="bg-muted p-3 rounded-md overflow-x-auto mb-2 text-xs">{children}</pre>
            ),
            blockquote: ({ children }) => (
              <blockquote className="border-l-4 border-primary pl-3 italic text-muted-foreground mb-2">
                {children}
              </blockquote>
            ),
            hr: () => <hr className="my-3 border-border" />,
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    )
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (inputValue.trim() && !disabled && !isProcessing) {
      onSendMessage(inputValue.trim())
      setInputValue('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Bot className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Welcome to Adya</h3>
            <p className="text-muted-foreground max-w-md">
              Upload your meeting notes or transcript to generate a comprehensive project proposal. 
              I'll analyze your requirements and create a detailed proposal using our 7-stage workflow.
            </p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                "flex gap-3",
                message.type === 'user' ? "justify-end" : "justify-start"
              )}
            >
              {message.type !== 'user' && (
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                    <Bot className="w-4 h-4 text-primary-foreground" />
                  </div>
                </div>
              )}
              
              <Card className={cn(
                "max-w-[80%]",
                message.type === 'user' 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-muted"
              )}>
                <CardContent className="p-3">
                  {message.metadata?.agentName && (
                    <div className="text-xs opacity-75 mb-1">
                      {message.metadata.agentName}
                      {message.metadata.stage && ` • ${message.metadata.stage}`}
                    </div>
                  )}

                  {renderMessageContent(message.content, message.type)}
                  
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-xs opacity-75">
                      {formatTimestamp(message.timestamp)}
                    </div>
                    
                    {message.metadata?.status && message.metadata.status !== 'complete' && (
                      <div className="flex items-center gap-1 text-xs opacity-75">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {message.metadata.status}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {message.type === 'user' && (
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                    <User className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              )}
            </div>
          ))
        )}
        
        {isProcessing && (
          <div className="flex gap-3 justify-start">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <Bot className="w-4 h-4 text-primary-foreground" />
              </div>
            </div>
            <Card className="bg-muted">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing your request...
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t p-4">
        {suggestions.length > 0 && (
          <div className="mb-3">
            <div className="text-xs text-muted-foreground mb-2">Suggested next actions</div>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion, index) => (
                <Button
                  key={`${index}_${suggestion}`}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  disabled={disabled || isProcessing}
                  onClick={() => {
                    const text = suggestion.trim()
                    if (!text || disabled || isProcessing) return
                    onSendMessage(text)
                  }}
                >
                  {suggestion}
                </Button>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex gap-2">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={disabled ? "Upload a file to start..." : "Ask questions about your proposal or request changes..."}
            disabled={disabled || isProcessing}
            className={cn(
              "flex-1 min-h-[40px] max-h-[120px] resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
              "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50"
            )}
            rows={1}
          />
          <Button 
            type="submit" 
            size="icon"
            disabled={!inputValue.trim() || disabled || isProcessing}
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  )
}
